// import core components
import { useEffect } from "react";
import dynamic from "next/dynamic";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";

// import custom component
import Hero from "@/elements/Hero";
import SEO from "@/elements/Shared/SEO";
const WorkTogether = dynamic(() => import("@/elements/WorkTogether"));
const StepCard = dynamic(() => import("@/elements/Shared/StepCard"));
const BlockTextImage = dynamic(
  () => import("@/elements/Shared/BlockTextImage")
);
const Circle = dynamic(
  () => import("@/elements/Shared/DesignComponents/Circle")
);
const DegradeEffect = dynamic(
  () => import("@/elements/Shared/DesignComponents/DegradeEffect")
);
const WhiteSquare = dynamic(
  () => import("@/elements/Shared/DesignComponents/WhiteSquare")
);

// import interfaces
import MenuOptions from "@/interfaces/MenuOptions";

// import data
import about from "@/data/pages/about/index.json";

// import styles
import styles from "./about.module.scss";

// import animations
import { processStepAnimation, valueStepAnimation } from "@/utils/pages/about";

type Props = {
  menuOptions: MenuOptions;
};

export default function Teams({ menuOptions }: Props) {
  const { seo, hero, axe_resultats, mission, process, valeurs, teams } = about;

  if (typeof window !== "undefined") {
    ScrollTrigger.getAll().forEach((t) => {
      t.kill();
    });
  }

  useEffect(() => {
    menuOptions.setMenuColor("white");
  }, []);

  return (
    <>
      <SEO seo={seo} />

      <main className={styles["main"]}>
        <Hero
          data={hero}
          isSquareDesignVisible={false}
          dispositionContainer={8}
          pageName="about"
          heroId="hero--degrade"
        />

        <section className={styles["axe-resultats"]}>
          <BlockTextImage
            content={axe_resultats.content}
            title={axe_resultats.title}
            images={axe_resultats.images}
            circles={axe_resultats.circles}
            HtagLevel={2}
            isImageAllEgal={true}
            dispositionContainer={20}
            sectionName="axe-resultats"
            optionsAnimImages={{
              type: "slideScale",
              firstImageToAnimate: 1,
              lastImageToAnimate: 2,
              xPercent: 200,
              yPercent: 1000,
              xScale: 40,
              yScale: -85,
              scale: 0.2,
              duration: 0.5,
              delayScale: 0.2,
              opacity: 0,
            }}
          />
        </section>

        <section className={styles["mission-and-process"]}>
          <DegradeEffect size={823} position={8} />

          <div className={styles["mission"]}>
            <BlockTextImage
              content={mission.content}
              title={mission.title}
              images={mission.images}
              circles={mission.circles}
              HtagLevel={2}
              isImageAllEgal={true}
              subtitleFontSizeStyle="small"
              dispositionContainer={21}
              sectionName="mission"
              optionsAnimImages={{
                type: "fadeMotionPath",
                svgPath: 2,
                start: 0.1,
                end: 0.135,
                duration: 1,
                alignOrigin: [0.35, 0.5],
              }}
            />
          </div>

          <div className={styles["process"]}>
            <BlockTextImage
              content={process.content}
              title={process.title}
              HtagLevel={2}
              isImageAllEgal={true}
              dispositionContainer={22}
              sectionName="process"
              externalAnimations={{
                externalAnimation: processStepAnimation,
                addTo: "imagesTl",
              }}
            >
              <div className={styles["process--content"]}>
                {process.steps.map(({ title, alt, icon, width, height }, i) => {
                  return (
                    <StepCard
                      stepId={`process-step-${i + 1}`}
                      key={alt + i}
                      title={title}
                      image={{ icon, alt, width, height }}
                      className={`process--step-card ${styles["process--step-card"]}`}
                    />
                  );
                })}
              </div>
            </BlockTextImage>
          </div>
          <Circle
            circle={{
              isAlternativeColor: false,
              isVisibleOnMobile: true,
              isAllSizeEgal: false,
              borderSize: "small",
              colorStyle: 5,
              styleNumber: 8,
            }}
            linearGradientId="a-propos-1"
            circleId="a-propos-circle-1"
            pathId="a-propos-path-1"
          />
          <Circle
            circleId="a-propos-circle-2"
            circle={{
              isSvg: true,
              isAlternativeColor: false,
              isVisibleOnMobile: true,
              isAllSizeEgal: false,
              borderSize: "small",
              colorStyle: 2,
              styleNumber: 8,
            }}
            linearGradientId="a-propos-2"
            pathId="a-propos-path-2"
          />
          <Circle
            circle={{
              isSvg: true,
              isAlternativeColor: false,
              isVisibleOnMobile: true,
              isAllSizeEgal: false,
              borderSize: "small",
              colorStyle: 5,
              styleNumber: 8,
            }}
            circleId="a-propos-circle-3"
            linearGradientId="a-propos-3"
            pathId="a-propos-path-3"
          />
          <Circle
            circle={{
              isSvg: false,
              isAlternativeColor: false,
              isVisibleOnMobile: true,
              isAllSizeEgal: false,
              borderSize: "small",
              colorStyle: 5,
              styleNumber: 8,
            }}
            circleId="a-propos-circle-4"
            linearGradientId="a-propos-4"
            pathId="a-propos-path-4"
          />
          <Circle
            circle={{
              isSvg: true,
              isAlternativeColor: false,
              isVisibleOnMobile: true,
              isAllSizeEgal: false,
              borderSize: "small",
              colorStyle: 2,
              styleNumber: 8,
            }}
            circleId="a-propos-circle-5"
            linearGradientId="a-propos-5"
            pathId="a-propos-path-5"
          />
        </section>

        <section className={styles["value-and-teams"]}>
          <div className={styles["value"]}>
            <BlockTextImage
              content={valeurs.content}
              title={valeurs.title}
              HtagLevel={2}
              isImageAllEgal={true}
              dispositionContainer={23}
              sectionName="valeurs"
              externalAnimations={{
                externalAnimation: valueStepAnimation,
                addTo: "imagesTl",
              }}
            >
              <div className={styles["value--content"]}>
                {valeurs.value_list.map(
                  ({ title, alt, icon, width, height }, i) => {
                    return (
                      <StepCard
                        stepId={`value-step-${i + 1}`}
                        key={alt + i}
                        title={title}
                        image={{ icon, alt, width, height }}
                        className={`valeurs--step-card ${styles["value--step-card"]}`}
                      />
                    );
                  }
                )}
              </div>
            </BlockTextImage>
          </div>

          <div className={styles["teams"]}>
            <BlockTextImage
              content={teams.content}
              title={teams.title}
              images={teams.images}
              circles={teams.circles}
              HtagLevel={2}
              isImageAllEgal={true}
              dispositionContainer={24}
              sectionName="team"
              optionsAnimImages={{
                type: "fadeMotionPath",
                svgPath: 2,
                start: 0,
                end: 0.1,
                duration: 1,
                alignOrigin: [0.25, 0.25],
              }}
            />
          </div>
          <Circle
            circle={{
              isAlternativeColor: false,
              isVisibleOnMobile: true,
              isAllSizeEgal: false,
              borderSize: "small",
              colorStyle: 5,
              styleNumber: 9,
            }}
            circleId="a-propos-circle-6"
            linearGradientId="a-propos-6"
            pathId="a-propos-path-6"
          />
          <Circle
            circle={{
              isSvg: true,
              isAlternativeColor: false,
              isVisibleOnMobile: true,
              isAllSizeEgal: false,
              borderSize: "small",
              colorStyle: 2,
              styleNumber: 9,
            }}
            circleId="a-propos-circle-7"
            pathId="a-propos-path-7"
            linearGradientId="a-propos-7"
          />
          <Circle
            circle={{
              isSvg: true,
              isAlternativeColor: false,
              isVisibleOnMobile: true,
              isAllSizeEgal: false,
              borderSize: "small",
              colorStyle: 5,
              styleNumber: 9,
            }}
            circleId="a-propos-circle-8"
            pathId="a-propos-path-8"
            linearGradientId="a-propos-8"
          />
          <WhiteSquare
            position="bottom-right"
            isDefaultColor={false}
            isLighter={true}
          />
        </section>

        <WorkTogether
          isDesignVisible={false}
          content="Vous recherchez une agence Web qui <strong>s'engage à fond dans votre développement?</strong> Faites-nous part de votre projet et nous bâtirons des solutions à la mesure de vos ambitions."
          buttonText="Décrivez-nous votre projet"
        />
      </main>
    </>
  );
}
